import { Component, OnInit, OnDestroy, Input, SimpleChanges, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, Validators, AbstractControl, FormGroup, FormArray, ValidationErrors } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Amline } from 'src/app/model/quote/amline/amline.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AmlineService } from '../amline.service';
import { ActivatedRoute } from "@angular/router";
import { QuoteService } from '../../quote.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  AMLIN_VEHICLE_OVERNIGHT_ADDRESSES,
} from '../amlin-horsebox-api.constants';

@Component({
  selector: 'app-vehicles',
  templateUrl: './vehicles.component.html',
  styleUrls: ['./vehicles.component.css', '../../quote.component.css']
})
export class VehiclesComponent implements OnInit, OnDestroy {
  staticAPIData: any;
  @Input() staticData;
  @Input() modelObj;
  @Input() apiModelObj;
  frmFields: any;
  submitted = false;
  @Input() authDetails;
  @Output() modelObjUpdated = new EventEmitter<Amline>();

  addressesForOvernight: any[] = [];
  overnightLookupCtx: { v: number; r: number } | null = null;
  private overnightLookupSubject = new Subject<{ postcode: string; v: number; r: number }>();
  private overnightLookupSubscription: Subscription;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef,
    private as: AmlineService,
    private route: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private quoteService: QuoteService,
  ) { }

  ngOnInit(): void {
    this.frmFields = this.fb.group({
      vehicles: this.fb.array([
        this.initVehicles()
      ]),
      drivers: this.fb.array([]),
    });
    this.patchValues();
    this.syncNcdValidationForAllVehicles();

    this.overnightLookupSubscription = this.overnightLookupSubject.pipe(debounceTime(400)).subscribe((ctx) => {
      this.doOvernightPostcodeLookup(ctx.postcode, ctx.v, ctx.r);
    });
  }

  ngOnDestroy(): void {
    this.overnightLookupSubscription?.unsubscribe();
  }

  static readonly MIN_VEHICLE_YEAR = 1900;
  maxVehicleYear = new Date().getFullYear();

  static vehicleYearValidator(ctrl: AbstractControl): ValidationErrors | null {
    const raw = ctrl.value;
    if (raw === null || raw === undefined || String(raw).trim() === '') return null;
    const year = Number(raw);
    if (!Number.isInteger(year) || year < VehiclesComponent.MIN_VEHICLE_YEAR) return { yearInvalid: true };
    if (year > new Date().getFullYear()) return { yearInFuture: true };
    return null;
  }

  static vehicleOvernightValidator(ctrl: AbstractControl): ValidationErrors | null {
    const kept = ctrl.get('is_kept_overnight')?.value;
    if (kept !== false) return null;
    const arr = ctrl.get('overnight_locations') as FormArray;
    if (!arr || arr.length < 1) return { overnightRequired: true };
    for (let j = 0; j < arr.length; j++) {
      const g = arr.at(j) as FormGroup;
      const postcode = (g.get('postcode')?.value || '').trim();
      if (!postcode) return { overnightLookupIncomplete: true };
    }
    return null;
  }

  ngDoCheck() {
    this.staticAPIData = this.staticData;
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  parentModelObj;
  drivingRestrictions: any = [];
  rating_type: string = '';
  ngOnChanges(changes: SimpleChanges) {
    this.parentModelObj = this.modelObj;

    if (this.apiModelObj) {
      this.modelObj = this.apiModelObj.amlin_data.data.attributes.application;
      this.modelObj.id = this.apiModelObj.amlin_data.data.id;
      this.modelObj.reference = this.apiModelObj.amlin_data.data.attributes.reference;
      this.modelObj.status = this.apiModelObj.amlin_data.data.attributes.status;
      this.modelObj.sequence_number = this.apiModelObj.sequence_number;

      this.rating_type = this.apiModelObj.amlin_data.data.attributes.application.rating_type;
    }

    if (this.staticData && this.modelObj && this.modelObj.proposer) {
      this.drivingRestrictions = (this.modelObj.proposer.proposer_type == 'company') ? this.staticData.driving_restrictions_commercial : this.staticData.driving_restrictions_personal;
    }
    this.patchValues();
  }

  get f(): { [key: string]: AbstractControl } {
    return this.frmFields.controls;
  }

  get vehicles(): FormArray {
    return this.frmFields.get("vehicles") as FormArray
  }

  get driversFullList(): FormArray {
    return this.frmFields.get("drivers") as FormArray
  }

  overnightLocationControls(vehicleIndex: number): FormArray {
    return this.vehicles.at(vehicleIndex).get('overnight_locations') as FormArray;
  }

  patchValues() {
    if (this.modelObj && this.modelObj.drivers) {
      this.drivers_list = this.modelObj.drivers;
    }
    if (!this.frmFields) {
      return;
    }
    if (this.frmFields && this.modelObj && this.modelObj.vehicles && this.modelObj.vehicles.length > 0) {

      this.patchVehicles();

      if (this.modelObj.drivers && this.modelObj.drivers.length > 0) {
        this.driversFullList.clear();
        this.modelObj.drivers.forEach((res) => {
          let dataCtrls = this.fb.group(res);
          this.driversFullList.push(dataCtrls);
        })
      }

      this.modelObj.vehicles.forEach((res, i) => {
        this.activateDriversSection(res.driving_restriction, i);

        let driver_equine = res.extended_use.driver_equine;
        let driver_non_equine = res.extended_use.driver_non_equine;
        let livestock_carriage = res.extended_use.livestock_carriage;
        let proposer_equine = res.extended_use.proposer_equine;
        let proposer_non_equine = res.extended_use.proposer_non_equine;

        if (driver_equine) {
          this.equineDriverControls(i).clear();
          res.equine_trade_drivers.forEach((d, idx) => {
            let drivers = this.modelObj.equine_trade_drivers.filter(x => x.id == d);
            if (drivers.length > 0) {
              drivers.forEach((dr) => {
                let dataCtrls = this.fb.group(dr);
                this.equineDriverControls(i).push(dataCtrls);
              })
            }
          })
        }

        if (driver_non_equine) {
          this.nonEquineDriverControls(i).clear();
          res.non_equine_trade_drivers.forEach((d, idx) => {
            let drivers = this.modelObj.non_equine_trade_drivers.filter(x => x.id == d);
            if (drivers.length > 0) {
              drivers.forEach((dr) => {
                let dataCtrls = this.fb.group(dr);
                this.nonEquineDriverControls(i).push(dataCtrls);
              })
            }
          })
        }

        let is_owner = (res.owner) ? false : true;
        this.enableOwner(is_owner, i);

        const keptVal = this.vehicles.at(i).get('is_kept_overnight')?.value;
        this.enablePostcode(keptVal, i);
        this.applyNcdValidation(i);
      })

    }
    this.syncNcdValidationForAllVehicles();
  }

  isNcdRequired(): boolean {
    const ratingType = this.modelObj?.rating_type || this.parentModelObj?.rating_type || null;
    return ratingType != 'fleet';
  }

  private syncNcdValidationForAllVehicles() {
    const vehiclesArr = this.frmFields?.get('vehicles') as FormArray | undefined;
    if (!vehiclesArr?.controls?.length) return;
    vehiclesArr.controls.forEach((_, idx) => this.applyNcdValidation(idx));
  }

  private applyNcdValidation(vehicleIndex: number) {
    const ncd = this.vehicles.at(vehicleIndex).get('ncd');
    if (!ncd) return;
    const required = this.isNcdRequired();
    if (required) {
      ncd.setValidators([Validators.required]);
      ncd.enable({ emitEvent: false });
    } else {
      ncd.clearValidators();
      ncd.setValue('', { emitEvent: false });
      ncd.disable({ emitEvent: false });
    }
    ncd.updateValueAndValidity({ emitEvent: false });
  }

  private overnightRowsFromListOrLegacy(listRaw: any[] | null, legacyPostcode: string): FormGroup[] {
    if (listRaw && listRaw.length > 0) {
      return listRaw.map((addr: any) => this.fb.group({
        search_postcode: [addr.postcode || ''],
        line1: [addr.line1 || ''],
        line2: [addr.line2 || ''],
        line3: [addr.line3 || ''],
        town: [addr.town || ''],
        county: [addr.county || ''],
        postcode: [addr.postcode || ''],
      }));
    }
    if (legacyPostcode) {
      return [this.fb.group({
        search_postcode: [legacyPostcode],
        line1: [''],
        line2: [''],
        line3: [''],
        town: [''],
        county: [''],
        postcode: [legacyPostcode],
      })];
    }
    return [this.initOvernightLocation()];
  }

  private overnightAddressListFromVehicle(res: any): any[] | null {
    const key = AMLIN_VEHICLE_OVERNIGHT_ADDRESSES;
    if (res[key] && Array.isArray(res[key]) && res[key].length > 0) return res[key];
    if (res.overnight_addresses && Array.isArray(res.overnight_addresses) && res.overnight_addresses.length > 0) {
      return res.overnight_addresses;
    }
    return null;
  }

  patchVehicles() {
    if (this.modelObj.vehicles.length > 0) {
      this.vehicles.clear();
      this.modelObj.vehicles.forEach((res, i) => {
        const listRaw = this.overnightAddressListFromVehicle(res);
        const hasSeparateOvernight = !!(res.postcode || (listRaw && listRaw.length > 0));
        const overnightRows = this.overnightRowsFromListOrLegacy(listRaw, res.postcode);

        let vehicles = this.fb.group({
          registration_number: [res.registration_number],
          make: [res.make],
          model: [res.model],
          year: [res.year, [VehiclesComponent.vehicleYearValidator]],
          weight: [res.weight],
          value: [res.value],
          cover_type: [res.cover_type],
          driving_restriction: [res.driving_restriction],
          horse_transporter: [res.horse_transporter],
          green_card: [res.green_card],
          mileage: [res.mileage],
          tracker: [res.tracker],
          ncd: [res.ncd],
          postcode: [res.postcode || ''],
          is_extended_use: [this.extendeduseButtons(res, i)],
          extended_use: this.fb.group({
            proposer_equine: [res.extended_use.proposer_equine],
            proposer_non_equine: [res.extended_use.proposer_non_equine],
            driver_equine: [res.extended_use.driver_equine],
            driver_non_equine: [res.extended_use.driver_non_equine],
            livestock_carriage: [res.extended_use.livestock_carriage],
          }),
          owner: (res.owner) ? res.owner : '',
          owner_name: (res.owner_name) ? res.owner_name : '',
          is_registered_owner: (res.owner) ? false : true,
          is_kept_overnight: [hasSeparateOvernight ? false : true],
          overnight_locations: this.fb.array(overnightRows),
          drivers: this.fb.array([]),
          non_equine_trade: [''],
          equine_trade_drivers: this.fb.array([]),
          non_equine_trade_drivers: this.fb.array([]),
        }, { validators: [VehiclesComponent.vehicleOvernightValidator] })
        if (!res.registration_number) {
          vehicles.get('is_registered_owner').setValue(2);
          vehicles.get('is_kept_overnight').setValue(2);
        }
        this.vehicles.push(vehicles);

        this.patchDrivers(res, i);

        if (res.extended_use.livestock_carriage) {
          this.horseTransporterSection = true;
          this.vehicles.at(i).get('horse_transporter').enable();
        }

      })
    }
  }

  onRegistrationNumberChange(event, index) {
    this.vehicles.controls[index].patchValue({
      "make": '',
      "model": '',
      "value": '',
      "weight": '',
      "year": '',
    });
    this.as.getVehicleDetails(event.target.value, this.authDetails)
      .subscribe(
        (res: any) => {
          if (res.meta) {
            this.vehicles.controls[index].patchValue({
              "make": res.meta.make,
              "model": res.meta.model,
              "value": res.meta.value,
              "weight": res.meta.weight,
              "year": res.meta.year,
            });
          }
        }, err => {
          if (err.error && err.error.errors) {
            this._snackBar.open('Vehicle not found.', '', {
              duration: 5000,
              panelClass: 'text-danger'
            });

          }
        },);

  }

  extendeduseButtons(res, index) {
    if (res && res.extended_use) {
      let driver_equine = res.extended_use.driver_equine;
      let driver_non_equine = res.extended_use.driver_non_equine;
      let livestock_carriage = res.extended_use.livestock_carriage;
      let proposer_equine = res.extended_use.proposer_equine;
      let proposer_non_equine = res.extended_use.proposer_non_equine;
      let is_extended_use = null;
      if (this.modelObj.id) {
        if (driver_equine == false && driver_non_equine == false && livestock_carriage == false && proposer_equine == false && proposer_non_equine == false) {
          is_extended_use = false;
        } else {
          is_extended_use = true;
        }
      }
      return is_extended_use;
    }
  }


  patchDrivers(res, i) {
    if (this.modelObj.drivers.length > 0) {
      this.driverControls(i).clear();
      this.modelObj.drivers.forEach((res1) => {
        let data = this.fb.group({ 'id': res1.id })
        this.driverControls(i).push(data);
      })
    }
  }

  helptext = '';
  helpTextPopup(content, text) {
    this.modalService.open(content, { centered: true });
    this.helptext = text;
  }

  initOvernightLocation(): FormGroup {
    return this.fb.group({
      search_postcode: [''],
      line1: [''],
      line2: [''],
      line3: [''],
      town: [''],
      county: [''],
      postcode: [''],
    });
  }

  initVehicles() {
    return this.fb.group({
      registration_number: ['', Validators.required],
      make: ['', Validators.required],
      model: ['', Validators.required],
      year: ['', [Validators.required, VehiclesComponent.vehicleYearValidator]],
      weight: ['', Validators.required],
      value: ['', Validators.required],
      cover_type: ['', Validators.required],
      driving_restriction: ['', Validators.required],
      is_extended_use: ['', Validators.required],
      extended_use: this.extendedUse(),
      non_equine_trade: ['', Validators.required],
      drivers: this.fb.array([]),
      equine_trade_drivers: this.fb.array([]),
      non_equine_trade_drivers: this.fb.array([]),
      horse_transporter: ['', Validators.required],
      green_card: ['', Validators.required],
      mileage: ['', Validators.required],
      is_registered_owner: ['', Validators.required],
      owner: ['', Validators.required],
      owner_name: [''],
      is_kept_overnight: ['', Validators.required],
      postcode: [''],
      overnight_locations: this.fb.array([this.initOvernightLocation()]),
      tracker: ['', Validators.required],
      ncd: [''],
    }, { validators: [VehiclesComponent.vehicleOvernightValidator] })
  }

  extendedUse() {
    return this.fb.group({
      proposer_equine: [''],
      proposer_non_equine: [''],
      driver_equine: [''],
      driver_non_equine: [''],
      livestock_carriage: [''],
    })
  }

  onYearInput(event: any, index: number) {
    const input = event.target;
    if (input.value.length > 4) {
      input.value = input.value.slice(0, 4);
      this.vehicles.at(index)?.get('year')?.setValue(input.value);
    }
  }

  get extendedUseControls() {
    return ((this.initVehicles().get('extended_use') as FormGroup).controls)
  }

  onExtendedUseChange(event, vehicle_index: number) {
    this.vehicles.at(vehicle_index).get('extended_use').disable();
    this.vehicles.at(vehicle_index).get('non_equine_trade').disable();
    this.vehicles.at(vehicle_index).get('equine_trade_drivers').disable();
    this.vehicles.at(vehicle_index).get('non_equine_trade_drivers').disable();
    this.horseTransporterSection = false;
    this.vehicles.at(vehicle_index).get('horse_transporter').disable();
    if (event.value == false) {
    } else {
      this.vehicles.at(vehicle_index).get('extended_use').enable();
    }
  }

  is_proposer_non_equine: boolean = false;
  onproposerNonEquineChange(event, vehicle_index: number) {
    if (event.checked) {
      this.is_proposer_non_equine = true;
      this.vehicles.at(vehicle_index).get('non_equine_trade').enable();
    } else {
      this.is_proposer_non_equine = false;
      this.vehicles.at(vehicle_index).get('non_equine_trade').disable();
    }
  }

  is_driver_equine: boolean = false;
  onDriverEquineChange(event, vehicle_index: number) {
    if (event.checked) {
      this.is_driver_equine = true;
      this.vehicles.at(vehicle_index).get('equine_trade_drivers').enable();
    } else {
      this.is_driver_equine = false;
      this.vehicles.at(vehicle_index).get('equine_trade_drivers').disable();
    }
  }

  is_driver_non_equine: boolean = false;
  onDriverNonEquineChange(event, vehicle_index: number) {
    if (event.checked) {
      this.is_driver_non_equine = true;
      this.vehicles.at(vehicle_index).get('non_equine_trade_drivers').enable();
    } else {
      this.is_driver_non_equine = false;
      this.vehicles.at(vehicle_index).get('non_equine_trade_drivers').disable();
    }
  }

  horseTransporterSection: boolean = false;
  onLivestockCarriageChange(event, vehicle_index: number) {
    if (event.checked) {
      this.horseTransporterSection = true;
      this.vehicles.at(vehicle_index).get('horse_transporter').enable();
    } else {
      this.horseTransporterSection = false;
      this.vehicles.at(vehicle_index).get('horse_transporter').disable();
    }
  }

  initComplexListDrivers() {
    return this.fb.group({
      id: ['', Validators.required],
      title: ['', Validators.required],
      first_name: ['', Validators.required],
      last_name: [''],
      dob: ['', Validators.required],
      licence_type: ['', Validators.required],
      licence_length: ['', Validators.required],
    })
  }
  initDrivers() {
    return this.fb.group({
      'id': ['', [Validators.required]]
    })
  }

  driverControls(vehicle_index: number): FormArray {
    return this.vehicles.at(vehicle_index).get("drivers") as FormArray
  }

  addExistingDriver(vehicle_index: number, type: number = 1) {
    switch (type) {
      case 1:
        this.driverControls(vehicle_index).push(this.initDrivers());
        break;
      case 2:
        this.equineDriverControls(vehicle_index).push(this.initDrivers());
        break;
      case 3:
        this.nonEquineDriverControls(vehicle_index).push(this.initDrivers());
        break;
    }

  }

  current_vehicle_index;
  driver_type: number = 1;
  addNewDriver(vehicle_index: number, drivers, type: number = 1) {
    this.driver_title = '';
    this.driver_forename = '';
    this.driver_surname = '';
    this.dont_have_driver_name = false;
    this.modalService.open(drivers, { centered: true });
    this.current_vehicle_index = vehicle_index;
    this.driver_type = type;
  }

  removeDrivers(vehicle_index: number, driver_index: number, type: number = 1) {
    switch (type) {
      case 1:
        this.driverControls(vehicle_index).removeAt(driver_index);
        break;
      case 2:
        this.equineDriverControls(vehicle_index).removeAt(driver_index);
        break;
      case 3:
        this.nonEquineDriverControls(vehicle_index).removeAt(driver_index);
        break;
    }
  }

  equineDriverControls(vehicle_index: number): FormArray {
    return this.vehicles.at(vehicle_index).get("equine_trade_drivers") as FormArray
  }

  nonEquineDriverControls(vehicle_index: number): FormArray {
    return this.vehicles.at(vehicle_index).get("non_equine_trade_drivers") as FormArray
  }

  dont_have_driver_name: boolean = false;
  onNoDriverNameChange(event) {
    if (event.checked) {
      this.driver_title = '';
      this.driver_forename = '';
      this.driver_surname = '';
      this.dont_have_driver_name = true;
    } else {
      this.dont_have_driver_name = false;
    }
  }

  driver_title;
  driver_forename;
  driver_surname;
  drivers_list = [];
  UniqueID: string = '';
  validationMsgForPopUp: string = '';
  createNewDriver() {
    if (!this.dont_have_driver_name) {
      if (!this.driver_title || !this.driver_forename) {
        this.validationMsgForPopUp = "Please select the title and enter driver's forename.";
        return;
      }
    } else {
      this.driver_forename = 'No name provided';
    }
    this.as.getUniqueID(this.authDetails)
      .subscribe(
        (response: any) => {
          if (response.meta) {
            this.UniqueID = response.meta.id;
            let data = {
              'id': response.meta.id,
              'title': this.driver_title,
              'first_name': this.driver_forename,
              'last_name': this.driver_surname,
            };
            this.drivers_list.push(data);

            let dataCtrls = this.fb.group(data)

            switch (this.driver_type) {
              case 1:
                this.driverControls(this.current_vehicle_index).push(dataCtrls);
                break;
              case 2:
                this.equineDriverControls(this.current_vehicle_index).push(dataCtrls);
                break;
              case 3:
                this.nonEquineDriverControls(this.current_vehicle_index).push(dataCtrls);
                break;
            }

            this.driversFullList.push(dataCtrls)

            this.modalService.dismissAll();
          }
        },
        (error) => {
          ;
        }
      );


  }

  onRegisteredOwnerChange(event, vehicle_index: number) {
    this.enableOwnerField(event.value, vehicle_index);
  }

  enableOwnerField(value, vehicle_index) {
    this.enableOwner(value, vehicle_index);
  }

  enableOwner(value, vehicle_index) {
    if (!value) {
      this.vehicles.at(vehicle_index).get('owner').enable();
    } else {
      this.vehicles.at(vehicle_index).get('owner').disable();
    }
  }

  onkeptOvernightChange(event, vehicle_index: number) {
    this.enablePostcodeIfKeptOvernight(event.value, vehicle_index);
    const g = this.vehicles.at(vehicle_index) as FormGroup;
    g.updateValueAndValidity();
  }

  enablePostcodeIfKeptOvernight(value, vehicle_index) {
    this.enablePostcode(value, vehicle_index);
  }

  enablePostcode(value, vehicle_index) {
    const arr = this.overnightLocationControls(vehicle_index);
    if (value == null || value === 2) {
      arr.disable({ emitEvent: false });
    } else if (!value) {
      arr.enable({ emitEvent: false });
    } else {
      arr.disable({ emitEvent: false });
    }
    (this.vehicles.at(vehicle_index) as FormGroup).updateValueAndValidity({ emitEvent: false });
  }

  addOvernightRow(vehicleIndex: number) {
    this.overnightLocationControls(vehicleIndex).push(this.initOvernightLocation());
    (this.vehicles.at(vehicleIndex) as FormGroup).updateValueAndValidity();
  }

  removeOvernightRow(vehicleIndex: number, rowIndex: number) {
    const arr = this.overnightLocationControls(vehicleIndex);
    if (arr.length <= 1) return;
    arr.removeAt(rowIndex);
    (this.vehicles.at(vehicleIndex) as FormGroup).updateValueAndValidity();
  }

  addProposerAddressToOvernight(vehicleIndex: number) {
    const proposerAddress = this.modelObj?.proposer?.address;
    if (!proposerAddress) return;

    const line1 = proposerAddress.line1 || '';
    const line2 = proposerAddress.line2 || '';
    const line3 = proposerAddress.line3 || '';
    const town = proposerAddress.town || '';
    const county = proposerAddress.county || '';
    const postcode = proposerAddress.postcode || '';
    if (!line1 || !postcode) return;
    const normalize = (value: string) => (value || '').trim().toUpperCase();
    const arr = this.overnightLocationControls(vehicleIndex);
    const alreadyExists = arr.controls.some((ctrl) => {
      const row = (ctrl as FormGroup).getRawValue();
      return normalize(row.line1) === normalize(line1) && normalize(row.postcode) === normalize(postcode);
    });
    if (alreadyExists) return;

    const emptyRowIdx = arr.controls.findIndex((ctrl) => {
      const row = (ctrl as FormGroup).getRawValue();
      return !normalize(row.line1) && !normalize(row.postcode);
    });
    const targetRow = this.fb.group({
      search_postcode: [postcode],
      line1: [line1],
      line2: [line2],
      line3: [line3],
      town: [town],
      county: [county],
      postcode: [postcode],
    });
    if (emptyRowIdx >= 0) {
      arr.at(emptyRowIdx).patchValue(targetRow.getRawValue());
    } else {
      arr.push(targetRow);
    }
    (this.vehicles.at(vehicleIndex) as FormGroup).updateValueAndValidity();
  }

  onOvernightPostcodeLookup(event: Event, v: number, r: number) {
    const postcode = ((event.target as HTMLInputElement)?.value || '').trim();
    this.overnightLookupCtx = { v, r };
    if (!postcode || postcode.length < 4) {
      this.addressesForOvernight = [];
      return;
    }
    this.overnightLookupSubject.next({ postcode, v, r });
  }

  private doOvernightPostcodeLookup(postcode: string, v: number, r: number) {
    this.quoteService.getAddress(postcode).subscribe({
      next: (response: any) => {
        const records = response?.records || response?.addresses || [];
        this.addressesForOvernight = Array.isArray(records) ? records : [];
        this.overnightLookupCtx = { v, r };
      },
      error: () => {
        this.addressesForOvernight = [];
      }
    });
  }

  overnightLookupOpen(v: number, r: number): boolean {
    return !!(this.overnightLookupCtx?.v === v && this.overnightLookupCtx?.r === r && this.addressesForOvernight.length);
  }

  selectOvernightAddress(record: any, v: number, r: number) {
    if (!record) return;
    const row = this.overnightLocationControls(v).at(r) as FormGroup;
    if (record.id != null && record.id !== '') {
      this.quoteService.getAddressById(record.id).subscribe({
        next: (full: any) => {
          this.applyAddressToOvernightRow(row, full);
          this.addressesForOvernight = [];
          this.overnightLookupCtx = null;
          (this.vehicles.at(v) as FormGroup).updateValueAndValidity();
        },
        error: () => { }
      });
      return;
    }
    this.applyAddressToOvernightRow(row, record);
    this.addressesForOvernight = [];
    this.overnightLookupCtx = null;
    (this.vehicles.at(v) as FormGroup).updateValueAndValidity();
  }

  private applyAddressToOvernightRow(row: FormGroup, address: any) {
    if (!address || !row) return;
    const a = address;
    const line1 = a.line1 ?? a.line_1 ?? a.Line1 ?? '';
    const line2 = a.line2 ?? a.line_2 ?? a.Line2 ?? '';
    const line3 = a.line3 ?? a.line_3 ?? a.Line3 ?? '';
    const town = a.town ?? a.Town ?? '';
    const county = a.county ?? a.County ?? '';
    const postcode = a.postcode ?? a.Postcode ?? '';
    row.patchValue({
      line1: line1 || '',
      line2: line2 || '',
      line3: line3 || '',
      town: town || '',
      county: county || '',
      postcode: postcode || '',
      search_postcode: postcode || row.get('search_postcode')?.value || '',
    });
  }

  addHorsebox() {
    this.vehicles.push(this.initVehicles());
    this.applyNcdValidation(this.vehicles.length - 1);
  }

  removeHorseboxes(index: number) {
    this.vehicles.removeAt(index);
  }

  activateDrivers: boolean = false;
  onDrivingRestrictionChange(event, vehicle_index) {
    this.vehicles.at(vehicle_index).get('drivers').disable();
    this.activateDriversSection(event.value, vehicle_index);
  }

  activateDriversSection(value, vehicle_index) {
    value = value.toLowerCase();
    switch (value) {
      case 'insured and spouse':
        this.activateDrivers = true;
        break;
      case 'insured and named':
        this.activateDrivers = true;
        break;
      case 'named drivers':
        this.activateDrivers = true;
        break;
      case 'named drivers excluding policyholder':
        this.activateDrivers = true;
        break;
      case 'any driver aged over 21 other than those named':
        this.activateDrivers = true;
        break;
      case 'any driver aged over 25 other than those named':
        this.activateDrivers = true;
        break;
      case 'any driver aged over 30 other than those named':
        this.activateDrivers = true;
        break;
      default:
        this.activateDrivers = false;
        break;
    }

    if (this.modelObj.drivers.length > 0) {
      this.driverControls(vehicle_index).clear();
      this.modelObj.drivers.forEach((res1) => {
        if (res1.id) {
          let data = this.fb.group({ 'id': res1.id })
          this.driverControls(vehicle_index).push(data);
        }
      })
    }
  }

  addIndividualExistingDriver() {

  }

  private transformVehicleForPersistence(v: any): any {
    const kept = v.is_kept_overnight;
    const locations = kept === false ? (v.overnight_locations || []).map((row: any) => ({
      line1: row.line1 || '',
      line2: row.line2 || '',
      line3: row.line3 || '',
      town: row.town || '',
      county: row.county || '',
      postcode: row.postcode || '',
    })) : [];
    if (kept === false && locations.length > 0 && v.postcode && !(locations[0].postcode || '').trim()) {
      locations[0] = { ...locations[0], postcode: (v.postcode || '').trim() };
    }
    const postcode = kept === false && locations.length ? locations[0].postcode : null;
    const { overnight_locations, ...rest } = v;
    const payload: any = {
      ...rest,
      postcode,
      [AMLIN_VEHICLE_OVERNIGHT_ADDRESSES]: locations,
    };
    return payload;
  }

  private mergeMissingAddressFieldsFromSubmittedPayload(submittedAttributes: any) {
    if (!this.modelObj || !submittedAttributes) return;
    const submittedVehicles = Array.isArray(submittedAttributes.vehicles) ? submittedAttributes.vehicles : [];
    if (Array.isArray(this.modelObj.vehicles) && this.modelObj.vehicles.length > 0) {
      this.modelObj.vehicles = this.modelObj.vehicles.map((vehicle: any, idx: number) => {
        const submittedVehicle = submittedVehicles[idx] || {};
        const responseOvernight = vehicle?.[AMLIN_VEHICLE_OVERNIGHT_ADDRESSES];
        const submittedOvernight = submittedVehicle?.[AMLIN_VEHICLE_OVERNIGHT_ADDRESSES];
        const shouldBackfillOvernight = (!Array.isArray(responseOvernight) || responseOvernight.length === 0) && Array.isArray(submittedOvernight) && submittedOvernight.length > 0;
        return shouldBackfillOvernight
          ? { ...vehicle, [AMLIN_VEHICLE_OVERNIGHT_ADDRESSES]: submittedOvernight, postcode: vehicle?.postcode || submittedVehicle?.postcode || null }
          : vehicle;
      });
    }
  }

  onSubmit() {
    this.submitted = true;
    this.frmFields.updateValueAndValidity();
    if (!this.frmFields.valid) return false;
    let formData = this.frmFields.getRawValue();
    this.modelObj.vehicles = formData.vehicles.map((v) => this.transformVehicleForPersistence(v));
    const proposerAddress = this.modelObj?.proposer?.address || {};
    this.modelObj.correspondence_address = {
      line1: proposerAddress.line1 || '',
      line2: proposerAddress.line2 || '',
      line3: proposerAddress.line3 || '',
      town: proposerAddress.town || '',
      county: proposerAddress.county || '',
      postcode: proposerAddress.postcode || '',
    };

    this.modelObj.drivers.forEach((res) => {
      let frmDrivers = formData.drivers.filter(x => x.id == res.id);
      if (frmDrivers.length > 0) {
        frmDrivers[0].claims = res.claims;
        frmDrivers[0].convictions = res.convictions;
      }

    })

    if (formData.drivers && formData.drivers.length == 1) {
      formData.drivers.forEach((res) => {
        if (res.first_name) {
          var drivers = this.modelObj.drivers.filter(x => x.id == res.id);
          if (drivers.length == 0) {
            this.modelObj.drivers.push(res);
          }
        }
      })

    }

    let drivers = [];
    let non_equine_trade_drivers = [];
    let equine_trade_drivers = [];
    formData.vehicles.forEach((res) => {
      if (res.drivers) {
        drivers = [];
        res.drivers.forEach((res1) => {
          drivers.push(res1.id);
        })
      }
      if (res.non_equine_trade_drivers) {
        res.non_equine_trade_drivers.forEach((res1) => {
          non_equine_trade_drivers.push(res1.id);
        })
      }
      if (res.equine_trade_drivers) {
        res.equine_trade_drivers.forEach((res1) => {
          equine_trade_drivers.push(res1.id);
        })
      }
    })

    this.modelObj.vehicles.forEach((res) => {
      res.drivers = drivers;
      res.non_equine_trade_drivers = non_equine_trade_drivers;
      res.equine_trade_drivers = equine_trade_drivers;
      res.breakdown_cover = false;
    })



    if (this.modelObj.type == 2) {
      this.modelObj.policy_id = null;
      this.modelObj.effective_date = null;
    }

    let data = {
      data: {
        "type": this.modelObj.type,
        "attributes": this.modelObj,
        "id": this.modelObj.id,
        "broker_id": this.modelObj.broker_id,
        "sequence_number": this.modelObj.sequence_number,
      }
    };
    const submittedAttributesForFallback = data?.data?.attributes;

    this.as.saveQuote(data)
      .subscribe(
        (res: any) => {
          if (res.success) {
            if (res.result.quote_data && res.result.quote_data.quote_versions[0].amlin_data) {
              this.modelObj = res.result.quote_data.quote_versions[0].amlin_data.attributes.application;
              this.modelObj.id = res.result.quote_data.quote_versions[0].amlin_data.id;
              this.modelObj.broker_id = res.result.quote_data.quote_versions[0].broker_id;
              this.modelObj.reference = res.result.quote_data.quote_versions[0].amlin_data.attributes.reference;
              this.modelObj.status = res.result.quote_data.quote_versions[0].amlin_data.attributes.status;
              this.modelObj.company_id = res.result.quote_data.quote_versions[0].company_id;
              this.modelObj.is_send_email = res.result.quote_data.quote_versions[0].is_send_email;
              this.modelObj.broker_commission = res.result.quote_data.quote_versions[0].financial_details.broker_commission;
              this.modelObj.broker_admin_fee = res.result.quote_data.quote_versions[0].financial_details.broker_admin_fee;
              this.modelObj.proposer.email = res.result.quote_data.quote_versions[0].policy_holder.email;
              this.modelObj.proposer.phone = res.result.quote_data.quote_versions[0].policy_holder.phone;
              this.modelObj.proposer.is_email = res.result.quote_data.quote_versions[0].policy_holder.is_email;
              this.modelObj.proposer.is_phone = res.result.quote_data.quote_versions[0].policy_holder.is_phone;
              this.modelObj.proposer.sms = res.result.quote_data.quote_versions[0].policy_holder.sms;
              this.modelObj.proposer.post = res.result.quote_data.quote_versions[0].policy_holder.post;
              this.modelObj.vehicles.forEach((res) => {
                res.breakdown_cover = false;
                let drivers: any[] = [];
                if (res.drivers && res.drivers.length > 0) {
                  res.drivers.forEach((d: any) => {
                    if (d.driver) {
                      drivers.push(d.driver);
                    }
                  })
                }

                res.drivers = drivers;
              })
              this.mergeMissingAddressFieldsFromSubmittedPayload(submittedAttributesForFallback);
              this.modelObjUpdated.emit(this.modelObj);
            };
          } else {
            this._snackBar.open(res.message, '', {
              duration: 5000,
              panelClass: 'text-danger'
            });
          }
        });
  }

}
